import {
    ref,
    get,
    set,
    remove,
    onValue,
    off,
    serverTimestamp,
} from "firebase/database";
import { rtdb } from "../utils/firestore";
import {
    ChartLock,
    ChartLockAttempt,
    ChartLockStatus,
    ChartLockInfo,
} from "intake_shared/src/types";
import { v4 as uuidv4 } from "uuid";
import { parseFirebaseTimestamp } from "intake_shared/src/util";

// Browser tab management
const BROWSER_TAB_ID_KEY = "chart_lock_browser_tab_id";
const ACTIVITY_UPDATE_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes - how often the system updates the lock's "last activity"

let browserTabId: string;
let activityUpdateInterval: NodeJS.Timeout | undefined;

// Initialize browser tab ID
const getOrCreateBrowserTabId = (): string => {
    if (!browserTabId) {
        const storedBrowserTabId = localStorage.getItem(BROWSER_TAB_ID_KEY);
        if (storedBrowserTabId) {
            browserTabId = storedBrowserTabId;
        } else {
            browserTabId = uuidv4();
            localStorage.setItem(BROWSER_TAB_ID_KEY, browserTabId);
        }
    }
    return browserTabId;
};

// Initialize browser tab ID
getOrCreateBrowserTabId();

// Attempt to lock a chart for editing
export const lockChart = async (
    clientId: string,
    userEmail: string,
    userName: string
): Promise<{ success: boolean; lock?: ChartLock; error?: string }> => {
    try {
        const currentBrowserTabId = getOrCreateBrowserTabId();

        const existingLock = await getChartLock(clientId);

        if (existingLock) {
            if (existingLock.locked_by !== userEmail) {
                await recordLockAttempt(clientId, userEmail, userName);
                return {
                    success: false,
                    error: `Chart is currently being edited by ${existingLock.locked_by_name}`,
                };
            } else if (existingLock.browser_tab_id !== currentBrowserTabId) {
                // Same user but different browser tab/window
                return {
                    success: false,
                    error: "Chart is already open in another tab or window",
                };
            } else {
                await updateLockActivity(clientId);
                return { success: true, lock: existingLock };
            }
        }

        // Create new lock
        const lock: ChartLock = {
            client_id: clientId,
            locked_by: userEmail,
            locked_by_name: userName,
            locked_at: new Date(),
            last_activity: new Date(),
            browser_tab_id: currentBrowserTabId,
        };

        await set(ref(rtdb, `chart_locks/${clientId}`), {
            ...lock,
            locked_at: serverTimestamp(),
            last_activity: serverTimestamp(),
        });

        startActivityUpdates(clientId);

        return { success: true, lock };
    } catch (error) {
        console.error("Error locking chart:", error);
        return {
            success: false,
            error: "Failed to lock chart. Please try again.",
        };
    }
};

// Release a chart lock
export const releaseChartLock = async (
    clientId: string,
    userEmail: string
): Promise<boolean> => {
    try {
        const lock = await getChartLock(clientId);

        if (!lock) {
            return true;
        }

        if (lock.locked_by !== userEmail) {
            return false;
        }

        await remove(ref(rtdb, `chart_locks/${clientId}`));

        stopActivityUpdates();

        return true;
    } catch (error) {
        console.error("Error releasing chart lock:", error);
        return false;
    }
};

// Get current lock status for a chart
export const getChartLockInfo = async (
    clientId: string,
    currentUserEmail: string
): Promise<ChartLockInfo> => {
    try {
        // Ensure browser tab ID is initialized
        const currentBrowserTabId = getOrCreateBrowserTabId();

        const lock = await getChartLock(clientId);

        if (!lock) {
            return {
                status: ChartLockStatus.AVAILABLE,
                can_edit: true,
            };
        }

        const isLockedByMe = lock.locked_by === currentUserEmail;
        const isMyBrowserTab = lock.browser_tab_id === currentBrowserTabId;

        return {
            status: isLockedByMe
                ? ChartLockStatus.LOCKED_BY_ME
                : ChartLockStatus.LOCKED_BY_OTHER,
            lock,
            can_edit: isLockedByMe && isMyBrowserTab,
        };
    } catch (error) {
        console.error("Error getting chart lock info:", error);
        return {
            status: ChartLockStatus.AVAILABLE,
            can_edit: true,
        };
    }
};

// Listen to chart lock changes in real-time
export const subscribeToChartLock = (
    clientId: string,
    currentUserEmail: string,
    callback: (lockInfo: ChartLockInfo) => void
): (() => void) => {
    const lockRef = ref(rtdb, `chart_locks/${clientId}`);

    const handleValue = (snapshot: any) => {
        if (!snapshot.exists()) {
            callback({
                status: ChartLockStatus.AVAILABLE,
                can_edit: true,
            });
            return;
        }

        const lockData = snapshot.val();
        const lockedAt = parseFirebaseTimestamp(lockData?.locked_at);
        const lastActivity =
            parseFirebaseTimestamp(lockData?.last_activity) || lockedAt;

        if (!lockedAt) {
            callback({
                status: ChartLockStatus.AVAILABLE,
                can_edit: true,
            });
            return;
        }

        const lock: ChartLock = {
            ...lockData,
            locked_at: lockedAt,
            last_activity: lastActivity,
        } as ChartLock;

        const isLockedByMe = lock.locked_by === currentUserEmail;
        const isMyBrowserTab = lock.browser_tab_id === getOrCreateBrowserTabId();

        callback({
            status: isLockedByMe
                ? ChartLockStatus.LOCKED_BY_ME
                : ChartLockStatus.LOCKED_BY_OTHER,
            lock,
            can_edit: isLockedByMe && isMyBrowserTab,
        });
    };

    onValue(lockRef, handleValue, (error) => {
        console.error("Error listening to chart lock:", error);
        callback({
            status: ChartLockStatus.AVAILABLE,
            can_edit: true,
        });
    });

    return () => {
        off(lockRef, "value", handleValue);
    };
};

// Cleanup when service is no longer needed
export const destroyChartLockService = (): void => {
    stopActivityUpdates();
};

// get chart lock
const getChartLock = async (clientId: string): Promise<ChartLock | null> => {
    try {
        const lockRef = ref(rtdb, `chart_locks/${clientId}`);
        const snapshot = await get(lockRef);

        if (!snapshot.exists()) {
            return null;
        }

        const data = snapshot.val();

        const lockedAt = parseFirebaseTimestamp(data.locked_at);
        const lastActivity = parseFirebaseTimestamp(data.last_activity) || lockedAt;

        if (!lockedAt) {
            console.warn("Invalid chart lock timestamps, removing lock:", clientId);
            await remove(lockRef);
            return null;
        }

        return {
            ...data,
            locked_at: lockedAt,
            last_activity: lastActivity,
        } as ChartLock;
    } catch (error) {
        console.error("Error getting chart lock:", error);
        return null;
    }
};

const updateLockActivity = async (clientId: string): Promise<void> => {
    try {
        await set(
            ref(rtdb, `chart_locks/${clientId}/last_activity`),
            serverTimestamp()
        );
    } catch (error) {
        console.error("Error updating lock activity:", error);
    }
};

const recordLockAttempt = async (
    clientId: string,
    userEmail: string,
    userName: string
): Promise<void> => {
    try {
        const attempt: ChartLockAttempt = {
            client_id: clientId,
            user_email: userEmail,
            user_name: userName,
            attempted_at: new Date(),
            browser_tab_id: getOrCreateBrowserTabId(),
        };

        await set(ref(rtdb, `chart_lock_attempts/${uuidv4()}`), {
            ...attempt,
            attempted_at: serverTimestamp(),
        });
    } catch (error) {
        console.error("Error recording lock attempt:", error);
    }
};

const startActivityUpdates = (clientId: string): void => {
    stopActivityUpdates(); // Clear any existing interval

    updateLockActivity(clientId);

    activityUpdateInterval = setInterval(() => {
        updateLockActivity(clientId);
    }, ACTIVITY_UPDATE_INTERVAL_MS);
};

const stopActivityUpdates = (): void => {
    if (activityUpdateInterval) {
        clearInterval(activityUpdateInterval);
        activityUpdateInterval = undefined;
    }
};

// Track user activity (call this when user interacts with the chart)
export const trackUserActivity = (clientId: string): void => {
    updateLockActivity(clientId);
};

// Cleanup on page unload
window.addEventListener("beforeunload", () => {
    destroyChartLockService();
});
